import { c as _c } from "react/compiler-runtime";
import { clsx } from "@/helpers/clsx";
import { useMaxBreakpoint } from "@/helpers/useBreakpoint";
import { useIsSmall } from "@/helpers/useMediaQuery";
import { wrap } from "@motionone/utils";
import { motion, useAnimationFrame, useMotionTemplate, useMotionValue, useScroll, useSpring, useTransform, useVelocity, useWillChange } from "framer-motion";
import React, { useRef } from "react";
export const LogoTicker = ({
  logos
}: {
  logos: any;
}) => {
  const isSmall = useIsSmall();
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });
  const opacity = useTransform(scrollYProgress, [0.45, 0.7], [1, isSmall ? 0.2 : 0]);
  const blur = useTransform(scrollYProgress, [0.45, 0.6], [0, 5]);
  const scale = useTransform(scrollYProgress, [0.45, 0.6], [1, 0.45]);
  const blurValue = useMotionTemplate`blur(${blur}px)`;
  const isMobile = useMaxBreakpoint("sm");
  return <div ref={containerRef} className="max-w-full md:-mb-[250px] md:h-[600px]">
      <div className="top-[30vh] z-50 md:sticky">
        <motion.div className="-mb-3 max-w-full overflow-hidden pb-3" initial={{
        opacity: 0
      }} style={{
        opacity,
        filter: blurValue,
        scale: isMobile ? 1 : scale,
        originX: "5%",
        originY: "100%",
        z: 0.001
      }}>
          <div className="relative mt-24 flex flex-col space-x-4 space-y-4" style={{
          "--mask-size": "25%",
          mask: "linear-gradient(to right, transparent 0%, #000 var(--mask-size), #000 calc(100% - var(--mask-size)), transparent 100%)"
        } as any}>
            <Parallax baseVelocity={1}>
              {logos[0]?.map((logo, i) => <LogoItem key={logo.id} logo={logo} />)}
            </Parallax>
            <Parallax baseVelocity={-1}>
              {logos[1]?.map((logo_0, i_0) => <LogoItem key={logo_0.id} logo={logo_0} />)}
            </Parallax>
          </div>
        </motion.div>
      </div>
    </div>;
};
const LogoItem = t0 => {
  const $ = _c(13);
  const {
    logo
  } = t0;
  let t1;
  if ($[0] !== logo.color) {
    t1 = {
      background: logo.color
    };
    $[0] = logo.color;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== logo.className) {
    t2 = clsx("h-10 w-10 transform-gpu brightness-0 invert", logo.className);
    $[2] = logo.className;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = `${logo.id} logo`;
  const t4 = `/_static/sprites/logos.svg#${logo.id}`;
  let t5;
  if ($[4] !== t4) {
    t5 = <use href={t4} />;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t5) {
    t6 = <svg className={t2} aria-label={t3}>{t5}</svg>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t1 || $[11] !== t6) {
    t7 = <div className="flex-shrink-0 rounded-2xl p-6 shadow-lg shadow-black/40" style={t1}>{t6}</div>;
    $[10] = t1;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
};
interface ParallaxProps {
  children: React.ReactNode;
  baseVelocity: number;
  className?: string;
}
function Parallax(t0) {
  const $ = _c(18);
  const {
    children,
    baseVelocity: t1,
    className
  } = t0;
  const baseVelocity = t1 === undefined ? 5 : t1;
  useWillChange();
  const baseX = useMotionValue(0);
  const {
    scrollY
  } = useScroll();
  const scrollVelocity = useVelocity(scrollY);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      damping: 50,
      stiffness: 400
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const smoothVelocity = useSpring(scrollVelocity, t2);
  let t3;
  let t4;
  let t5;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [0, 1000];
    t4 = [0, 5];
    t5 = {
      clamp: false
    };
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
  } else {
    t3 = $[1];
    t4 = $[2];
    t5 = $[3];
  }
  const velocityFactor = useTransform(smoothVelocity, t3, t4, t5);
  const x = useTransform(baseX, _temp);
  const childrenCount = React.Children.count(children);
  const velocity = baseVelocity / childrenCount * 5;
  const directionFactor = useRef(1);
  let t6;
  if ($[4] !== baseX || $[5] !== velocity || $[6] !== velocityFactor) {
    t6 = (t, delta) => {
      let moveBy = directionFactor.current * velocity * (delta / 1000) * 4;
      if (velocityFactor.get() < 0) {
        directionFactor.current = -1;
      } else {
        if (velocityFactor.get() > 0) {
          directionFactor.current = 1;
        }
      }
      moveBy = moveBy + directionFactor.current * moveBy * velocityFactor.get();
      baseX.set(baseX.get() + moveBy);
    };
    $[4] = baseX;
    $[5] = velocity;
    $[6] = velocityFactor;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  useAnimationFrame(t6);
  let t7;
  if ($[8] !== className) {
    t7 = clsx("parallax", className);
    $[8] = className;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== x) {
    t8 = {
      x
    };
    $[10] = x;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== children || $[13] !== t8) {
    t9 = <motion.div className="scroller grid w-fit auto-cols-max grid-flow-col justify-center gap-4" style={t8}>{children}{children}</motion.div>;
    $[12] = children;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t7 || $[16] !== t9) {
    t10 = <div className={t7}>{t9}</div>;
    $[15] = t7;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  return t10;
}
function _temp(v) {
  return `${wrap(0, -50, v)}%`;
}